/**
 * AvatarCard 컴포넌트
 * @description 가로줄에 늘어놓는 사람 카드. 시안 `avatar-card`(`3041:2434`). 얼굴 하나에
 *   이름과, 왜 여기 있는지 한 줄. 사람만 오는 것은 아니다 — 가게·팬베이스도 섞인다. 그래서
 *   무엇인지 묻지 않고 얼굴·이름·한 줄만 받는다. 앱 `shared/ui/AvatarCard` 의 웹 포팅.
 * @example
 * <AvatarCard avatar={<Avatar type='image' size='sm' image={<AppImage uri={url} />} />}
 *   name={user.displayName} caption="3 collabs together" href={`/@${user.username}`} />
 */
import Link from "next/link";
import { memo } from "react";

import { avatarCardStyles } from "./AvatarCard.styles";
import type { AvatarCardProps } from "./AvatarCard.types";

const AvatarCard = memo<AvatarCardProps>(
  ({ avatar, name, caption, href, onPress, accessibilityLabel, testID }) => {
    const interactive = Boolean(href || onPress);
    const className = avatarCardStyles.root({ interactive });
    const content = (
      <>
        {avatar}
        <div className={avatarCardStyles.texts}>
          <p className={avatarCardStyles.name}>{name}</p>
          {caption ? (
            <p className={avatarCardStyles.caption}>{caption}</p>
          ) : null}
        </div>
      </>
    );

    if (href) {
      return (
        <Link
          href={href}
          className={className}
          aria-label={accessibilityLabel ?? name}
          data-testid={testID}
          draggable={false}
        >
          {content}
        </Link>
      );
    }
    if (!onPress) {
      return (
        <div className={className} data-testid={testID}>
          {content}
        </div>
      );
    }
    return (
      <button
        type="button"
        className={className}
        onClick={onPress}
        aria-label={accessibilityLabel ?? name}
        data-testid={testID}
      >
        {content}
      </button>
    );
  },
);

AvatarCard.displayName = "AvatarCard";

export default AvatarCard;
